import api from 'store/api';
import { setErrorCatch } from 'store/status/actions';
import {
  Schedule,
  SchedulesData,
  ScheduleFilters,
  SchedulePostMutationRequest,
  SchedulePatchMutationRequest,
} from './interfaces';

export const schedulesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSchedules: builder.query<SchedulesData, Partial<ScheduleFilters>>({
      query: (filters) => ({
        url: '/schedules',
        params: filters,
      }),
      providesTags: ['Schedules'],
      transformResponse: (response: SchedulesData) => ({
        ...response,
        'hydra:member': response['hydra:member'].sort(
          (schedule, comparedSchedule) =>
            schedule.name.localeCompare(comparedSchedule.name),
        ),
      }),
    }),
    getSchedule: builder.query<Schedule, string>({
      query: (id) => `/schedules/${id}`,
      providesTags: ['Schedules'],
    }),
    addSchedule: builder.mutation<Schedule, SchedulePostMutationRequest>({
      query: (body) => ({
        url: '/schedules',
        method: 'POST',
        body,
      }),
    }),
    updateSchedule: builder.mutation<Schedule, SchedulePatchMutationRequest>({
      query: ({ id, ...body }) => ({
        url: `/schedules/${id}`,
        headers: { 'content-type': 'application/merge-patch+json' },
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: updatedSchedule } = await queryFulfilled;

          const schedulesCache = api.util.selectInvalidatedBy(getState(), [
            { type: 'Schedules' },
          ]);

          schedulesCache
            .filter(({ endpointName }) => endpointName === 'getSchedules')
            .forEach(({ originalArgs }) => {
              dispatch(
                schedulesApi.util.updateQueryData(
                  'getSchedules',
                  originalArgs,
                  (draft) => {
                    const scheduleIndex = draft?.['hydra:member']?.findIndex(
                      (schedule) => schedule.id === id,
                    );
                    if (scheduleIndex !== -1) {
                      draft?.['hydra:member']?.splice(
                        scheduleIndex,
                        1,
                        updatedSchedule,
                      );
                    }
                  },
                ),
              );
            });

          schedulesCache
            .filter(
              ({ endpointName, originalArgs }) =>
                endpointName === 'getSchedule' && originalArgs === id,
            )
            .forEach(({ originalArgs }) => {
              dispatch(
                schedulesApi.util.updateQueryData(
                  'getSchedule',
                  originalArgs,
                  () => updatedSchedule,
                ),
              );
            });
        } catch (error) {
          dispatch(setErrorCatch(error));
        }
      },
    }),
  }),
});

export const {
  useGetSchedulesQuery,
  useGetScheduleQuery,
  useAddScheduleMutation,
  useUpdateScheduleMutation,
} = schedulesApi;
